import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../api/axios';

const STAGES = [
  { id: 'lead',        label: 'Lead',        color: 'border-t-slate-400',   hbg: 'bg-slate-50',   badge: 'bg-slate-200 text-slate-700' },
  { id: 'qualified',   label: 'Nitelikli',   color: 'border-t-blue-400',    hbg: 'bg-blue-50',    badge: 'bg-blue-100 text-blue-700' },
  { id: 'proposal',    label: 'Teklif',      color: 'border-t-violet-400',  hbg: 'bg-violet-50',  badge: 'bg-violet-100 text-violet-700' },
  { id: 'negotiation', label: 'Müzakere',    color: 'border-t-amber-400',   hbg: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700' },
  { id: 'won',         label: 'Kazanıldı',   color: 'border-t-emerald-400', hbg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' },
  { id: 'lost',        label: 'Kaybedildi',  color: 'border-t-red-400',     hbg: 'bg-red-50',     badge: 'bg-red-100 text-red-600' },
];

const AVATAR_COLORS = ['bg-blue-500','bg-pink-500','bg-indigo-500','bg-amber-500','bg-emerald-500','bg-purple-500','bg-rose-500','bg-teal-500'];
function avatarColor(str) {
  if (!str) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const ef = { title: '', customer_id: '', expected_value: '', stage: 'lead', notes: '' };

// ─── Deal Kartı ──────────────────────────────────────────────────────────────
function DealCard({ deal, index, customers, onClick }) {
  const customer = customers.find(c => String(c.id) === String(deal.customer_id));
  const initials = customer?.full_name ? customer.full_name[0].toUpperCase() : '?';
  const color = avatarColor(customer?.full_name);

  return (
    <Draggable draggableId={String(deal.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick(deal)}
          className={
            'bg-white rounded-xl border p-3 mb-2 select-none transition-all duration-200 cursor-pointer ' +
            (snapshot.isDragging
              ? 'shadow-2xl border-indigo-300 rotate-1 scale-105'
              : 'shadow-sm border-gray-100 hover:shadow-md hover:-translate-y-0.5')
          }
        >
          <p className="text-sm font-semibold text-gray-800 mb-1.5 leading-snug">{deal.title}</p>
          {customer && <p className="text-xs text-gray-400 mb-3">👤 {customer.full_name}{customer.company_name ? ' · ' + customer.company_name : ''}</p>}
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-emerald-600">
              {deal.expected_value ? Number(deal.expected_value).toLocaleString('tr-TR') + ' TL' : '—'}
            </span>
            <div className={'w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ' + color}>
              {initials}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

// ─── Kolon ───────────────────────────────────────────────────────────────────
function Column({ stage, deals, customers, onCardClick }) {
  const total = deals.reduce((s, d) => s + (Number(d.expected_value) || 0), 0);
  return (
    <div className={'flex flex-col bg-gray-50 rounded-2xl border-t-4 min-w-52 w-full max-w-64 flex-shrink-0 ' + stage.color}>
      <div className={'px-3 py-2.5 ' + stage.hbg}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-gray-700">{stage.label}</span>
          <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' + stage.badge}>{deals.length}</span>
        </div>
        {total > 0 && <div className="text-xs text-gray-500 mt-0.5">{total.toLocaleString('tr-TR')} TL</div>}
      </div>
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={'flex-1 p-2 min-h-28 rounded-b-2xl transition-colors ' + (snapshot.isDraggingOver ? 'bg-indigo-50' : '')}
          >
            {deals.map((deal, i) => (
              <DealCard key={deal.id} deal={deal} index={i} customers={customers} onClick={onCardClick} />
            ))}
            {provided.placeholder}
            {deals.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex items-center justify-center h-20 text-gray-300 text-xs">Buraya sürükle</div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ─── Deal Detay Modal ─────────────────────────────────────────────────────────
function DealModal({ deal, customers, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState({
    title: deal.title || '',
    customer_id: String(deal.customer_id || ''),
    expected_value: deal.expected_value || '',
    stage: deal.stage || 'lead',
    notes: deal.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, customer_id: form.customer_id || null, expected_value: form.expected_value ? Number(form.expected_value) : null };
      await api.put('/deals/' + deal.id, payload);
      onUpdated();
      onClose();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Güncelleme başarısız'));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await api.delete('/deals/' + deal.id); onDeleted(); onClose(); }
    catch (err) { alert('Hata: ' + (err.response?.data?.detail || 'Silme başarısız')); }
    finally { setDeleting(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal animate-slideup">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold">Deal Düzenle</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <form onSubmit={handleSave} className="space-y-3">
          <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="input" placeholder="Başlık *" />
          <select value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })} className="input">
            <option value="">Müşteri seçin</option>
            {customers.map(c => <option key={c.id} value={String(c.id)}>{c.full_name}{c.company_name ? ' · ' + c.company_name : ''}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" value={form.expected_value} onChange={e => setForm({ ...form, expected_value: e.target.value })} className="input" placeholder="Beklenen Değer (TL)" />
            <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })} className="input">
              {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="input resize-none" placeholder="Notlar" />
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Kaydediliyor...' : 'Güncelle'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">İptal</button>
          </div>
        </form>
        <div className="mt-3 pt-3 border-t border-gray-100">
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} className="text-xs text-red-400 hover:text-red-600 font-medium">Deal'i sil</button>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-xs text-red-500">Emin misiniz?</span>
              <button onClick={handleDelete} disabled={deleting} className="text-xs font-semibold text-red-600 hover:text-red-800">{deleting ? 'Siliniyor...' : 'Evet, sil'}</button>
              <button onClick={() => setConfirmDelete(false)} className="text-xs text-gray-400 hover:text-gray-600">Vazgeç</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Yeni Deal Modal ──────────────────────────────────────────────────────────
function NewDealModal({ customers, onClose, onCreated }) {
  const [form, setForm] = useState(ef);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, customer_id: form.customer_id || null, expected_value: form.expected_value ? Number(form.expected_value) : null };
      await api.post('/deals', payload);
      onCreated();
      onClose();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Oluşturma başarısız'));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal animate-slideup">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold">Yeni Fırsat</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="input" placeholder="Başlık *" />
          <select value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })} className="input">
            <option value="">Müşteri seçin</option>
            {customers.map(c => <option key={c.id} value={String(c.id)}>{c.full_name}{c.company_name ? ' · ' + c.company_name : ''}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" value={form.expected_value} onChange={e => setForm({ ...form, expected_value: e.target.value })} className="input" placeholder="Beklenen Değer (TL)" />
            <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })} className="input">
              {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="input resize-none" placeholder="Notlar" />
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Oluşturuluyor...' : 'Oluştur'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">İptal</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
export default function KanbanBoard() {
  const [deals, setDeals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [dealsRes, customersRes] = await Promise.all([api.get('/deals'), api.get('/customers')]);
      setDeals(dealsRes.data);
      setCustomers(customersRes.data);
    } catch (err) {
      console.error('Veri yüklenemedi:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragEnd = async ({ source, destination }) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Aynı stage içinde sıra değişimi — sadece local
    if (source.droppableId === destination.droppableId) {
      setDeals(prev => {
        const stageDeals = prev.filter(d => d.stage === source.droppableId);
        const others = prev.filter(d => d.stage !== source.droppableId);
        const [moved] = stageDeals.splice(source.index, 1);
        stageDeals.splice(destination.index, 0, moved);
        return [...others, ...stageDeals];
      });
      return;
    }

    // Stage değişimi — önce optimistic UI, sonra API
    const dealId = deals.filter(d => d.stage === source.droppableId)[source.index]?.id;
    if (!dealId) return;

    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: destination.droppableId } : d));
    setUpdatingId(dealId);
    try {
      await api.put('/deals/' + dealId, { stage: destination.droppableId });
    } catch {
      // Hata durumunda geri al
      await load();
      alert('Aşama güncellenemedi, lütfen tekrar deneyin.');
    } finally { setUpdatingId(null); }
  };

  const dealsByStage = (stageId) => deals.filter(d => d.stage === stageId);
  const wonRevenue = deals.filter(d => d.stage === 'won').reduce((s, d) => s + (Number(d.expected_value) || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Yükleniyor...</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-5 animate-fadein">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Satış Pipeline</h1>
          <p className="text-gray-500 text-sm">
            {deals.length} fırsat · {wonRevenue.toLocaleString('tr-TR')} TL kazanıldı
            {updatingId && <span className="ml-2 text-indigo-400 text-xs">● Güncelleniyor...</span>}
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary">+ Yeni Fırsat</button>
      </div>

      {/* Kanban */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {STAGES.map(stage => (
            <Column
              key={stage.id}
              stage={stage}
              deals={dealsByStage(stage.id)}
              customers={customers}
              onCardClick={setSelectedDeal}
            />
          ))}
        </div>
      </DragDropContext>

      {/* Modals */}
      {showNew && <NewDealModal customers={customers} onClose={() => setShowNew(false)} onCreated={load} />}
      {selectedDeal && (
        <DealModal
          deal={selectedDeal}
          customers={customers}
          onClose={() => setSelectedDeal(null)}
          onUpdated={load}
          onDeleted={load}
        />
      )}
    </div>
  );
}
